import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import type { RootState } from '../store';
import { setMessages, endInterview, setBehaviorSummary } from '../store/interviewSlice';
import LoadingTransition from '../components/LoadingTransition';
import {
  generateInterviewerResponse,
  generateReport,
} from '../services/aiService';
import {
  generateSalaryOpening,
  generateSalaryReport,
} from '../services/salaryAIService';
import { authFetch, auth } from '../services/auth';
import { trackInterviewComplete } from '../services/behaviorTracker';
import type { BehaviorSummary, Message, InterviewConfig, InterviewReport } from '../types';
import type { SalaryMessage } from '../types/salaryNegotiation';
import { App } from 'antd';

// 默认开场白（当API失败时使用）
function getDefaultOpening(overallScore: number): string {
  if (overallScore >= 90) {
    return `您好！首先恭喜您通过面试，技术团队对您的表现评价非常高。我是负责薪酬沟通的HR总监，想跟您聊聊薪资方面的事情。方便告诉我您目前的薪资情况吗？比如月薪总包大概是多少？`;
  } else if (overallScore >= 75) {
    return `您好，恭喜您通过面试！我是HR总监，想跟您沟通一下薪酬方面的事。首先想了解一下，您目前的薪资水平大概是怎样的？`;
  } else {
    return `您好，面试流程已经结束了。我是HR总监，想跟您沟通一下薪资。首先请问您目前的月薪是多少？`;
  }
}

// 加载阶段类型
type LoadingPhase = 'fetching' | 'entering' | 'ready';

export default function LoadingPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { message: antdMessage } = App.useApp();
  const config = useSelector((state: RootState) => state.interview.config);
  const messages = useSelector((state: RootState) => state.interview.messages);
  const [isPreparing, setIsPreparing] = useState(true);
  const [loadingPhase, setLoadingPhase] = useState<LoadingPhase>('fetching');
  const [displayMessage, setDisplayMessage] = useState('');
  const hasFetched = useRef(false);
  const isReady = useRef(false);

  // 从 location state 获取模式和数据
  const locationState = location.state as {
    mode?: 'prepare' | 'generate-report' | 'salary' | 'generate-salary-report';
    callDuration?: number;
    interviewConfig?: InterviewConfig;
    report?: InterviewReport;
    behaviorSummary?: BehaviorSummary;
    salaryConfig?: any;
    dialogueHistory?: any;
    finalOffer?: number;
  };
  const mode = locationState?.mode || 'prepare';
  const callDuration = locationState?.callDuration || 0;
  const behaviorSummary = locationState?.behaviorSummary;
  const isGenerateReportMode = mode === 'generate-report';
  const isSalaryMode = mode === 'salary';
  const isGenerateSalaryReportMode = mode === 'generate-salary-report';

  // 获取面试官/HR名字
  const getInterviewerName = () => {
    if (isSalaryMode) {
      return 'HR总监';
    }
    return config?.interviewer?.name || '面试官';
  };

  // 更新显示的文案
  useEffect(() => {
    const name = getInterviewerName();

    if (isGenerateReportMode || isGenerateSalaryReportMode) {
      // 报告生成模式的文案在对应函数中控制
      return;
    }

    if (isSalaryMode) {
      // 谈薪准备模式的文案
      switch (loadingPhase) {
        case 'fetching':
          setDisplayMessage(`${name}正在查看您的面试评价...`);
          break;
        case 'entering':
          setDisplayMessage(`${name}正在准备薪酬方案...`);
          break;
        case 'ready':
          setDisplayMessage('准备就绪，即将进入薪酬沟通');
          break;
      }
      return;
    }

    // 面试准备模式的文案
    switch (loadingPhase) {
      case 'fetching':
        // 根据是否有简历显示不同文案
        if (auth.getUser()?.resumeName) {
          setDisplayMessage(`${name}正在翻阅你的简历...`);
        } else {
          setDisplayMessage(`${name}正在调试设备...`);
        }
        break;
      case 'entering':
        setDisplayMessage(`${name}正在进入面试间...`);
        break;
      case 'ready':
        setDisplayMessage('准备就绪，即将进入面试间');
        break;
    }
  }, [
    loadingPhase,
    isGenerateReportMode,
    isSalaryMode,
    isGenerateSalaryReportMode,
    config?.interviewer?.name,
  ]);

  useEffect(() => {
    // 防止重复请求
    if (hasFetched.current) {
      return;
    }
    hasFetched.current = true;

    if (isGenerateReportMode) {
      // 报告生成模式
      generateReportData();
    } else if (isGenerateSalaryReportMode) {
      // 谈薪报告生成模式
      generateSalaryReportData();
    } else if (isSalaryMode) {
      // 谈薪准备模式
      prepareSalaryData();
    } else {
      // 面试准备模式
      prepareInterviewData();
    }
  }, []);

  // 谈薪准备
  const prepareSalaryData = async () => {
    const { interviewConfig, report } = locationState;

    if (!interviewConfig || !report) {
      navigate('/', { replace: true });
      return;
    }

    setIsPreparing(true);
    setLoadingPhase('fetching');

    try {
      // 调用接口获取开场白
      const salaryConfig = {
        interviewConfig,
        overallScore: report.totalScore,
        dimensions: report.dimensions, // 传递6个维度评分
      };
      const openingScript = await generateSalaryOpening(salaryConfig);

      // 进入准备阶段
      setLoadingPhase('entering');

      setTimeout(() => {
        setLoadingPhase('ready');
        isReady.current = true;

        // 将开场白作为对话记录
        const initialDialogue: SalaryMessage[] = [
          {
            role: 'hr',
            content: openingScript,
            timestamp: Date.now(),
          },
        ];

        setTimeout(() => {
          navigate('/salary-negotiation', {
            replace: true,
            state: {
              interviewConfig,
              report,
              initialDialogue, // 传递开场白对话记录到谈薪页
            },
          });
        }, 2000);
      }, 2000);
    } catch {
      // 出错时按默认流程走
      setLoadingPhase('entering');

      setTimeout(() => {
        setLoadingPhase('ready');
        isReady.current = true;

        // 将默认开场白作为对话记录
        const defaultOpeningScript = getDefaultOpening(report.totalScore);
        const initialDialogue: SalaryMessage[] = [
          {
            role: 'hr',
            content: defaultOpeningScript,
            timestamp: Date.now(),
          },
        ];

        setTimeout(() => {
          navigate('/salary-negotiation', {
            replace: true,
            state: {
              interviewConfig,
              report,
              initialDialogue, // 传递开场白对话记录到谈薪页
            },
          });
        }, 2000);
      }, 2000);
    } finally {
      setIsPreparing(false);
    }
  };

  // 生成报告
  const generateReportData = async () => {
    // 检查是否有面试数据
    if (!config || messages.length === 0) {
      navigate('/', { replace: true });
      return;
    }

    try {
      setIsPreparing(true);
      setDisplayMessage('正在生成面试报告');

      // 1. 生成报告（带行为分析数据）
      const report = await generateReport(config, messages, behaviorSummary);

      // 2. 保存行为摘要到 Redux
      if (behaviorSummary) {
        dispatch(setBehaviorSummary(behaviorSummary));
      }

      // 3. 保存报告到 Redux
      dispatch(endInterview(report));

      // 4. 保存到后端，传递面试官ID
      const saveResponse = await authFetch(
        `${import.meta.env.VITE_API_BASE_URL || ''}/api/interviews`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            jobType: config.jobType,
            jobLevel: config.level,
            interviewType: config.type,
            interviewerId: config.interviewer?.id,
            messages,
            report,
            score: report?.totalScore || 0,
            duration: callDuration,
            isPressureInterview: config.isPressureInterview,
            hasJobDescription: config.hasJobDescription,
            jobDescription: config.jobDescription,
            platform: 'web',
          }),
        },
      );

      if (!saveResponse.ok) {
        throw new Error('保存面试记录失败');
      }

      const saveData = await saveResponse.json();
      const interviewId = saveData.id;

      // 5. 记录面试完成行为
      trackInterviewComplete(interviewId, {
        score: report?.totalScore || 0,
        rounds: messages.filter((m) => m.role === 'user').length,
        duration: callDuration,
      }).catch(() => {
        // 记录失败不影响主流程
      });

      // 6. 更新展示文案，延迟2秒后跳转
      setDisplayMessage('报告已生成，正在跳转...');

      setTimeout(() => {
        // 跳转到报告页，URL 上拼接报告 ID，并标记来源为生成页
        navigate(`/report/${interviewId}?from=generating`, { replace: true });
      }, 2000);
    } catch (error) {
      // 保存失败也跳转到报告页面（会显示默认报告）
      antdMessage.error('保存面试记录失败，但仍可查看报告');
      navigate('/report', { replace: true });
    } finally {
      setIsPreparing(false);
    }
  };

  // 预生成面试官开场白
  const prepareInterviewData = async () => {
    // 检查是否有面试配置
    if (!config) {
      navigate('/', { replace: true });
      return;
    }

    try {
      setIsPreparing(true);
      setLoadingPhase('fetching');

      // 1. 调用 chat 接口生成开场白
      const welcomeContent = await generateInterviewerResponse(config, []);

      // 创建开场白消息
      const welcomeMessage: Message = {
        role: 'interviewer',
        content: welcomeContent,
        timestamp: Date.now(),
      };

      // 保存到 Redux
      dispatch(setMessages([welcomeMessage]));

      // 数据获取完成，进入"正在进入面试间"阶段
      setLoadingPhase('entering');

      // 停留2秒后进入准备就绪阶段
      setTimeout(() => {
        setLoadingPhase('ready');
        isReady.current = true;

        // 再停留2秒后跳转
        setTimeout(() => {
          navigate('/interview', { replace: true });
        }, 2000);
      }, 2000);
    } catch (err) {
      // 出错时也按正常流程走
      setLoadingPhase('entering');

      setTimeout(() => {
        setLoadingPhase('ready');
        isReady.current = true;

        setTimeout(() => {
          navigate('/interview', { replace: true });
        }, 2000);
      }, 2000);
    } finally {
      setIsPreparing(false);
    }
  };

  // 生成谈薪报告
  const generateSalaryReportData = async () => {
    const { salaryConfig, dialogueHistory, finalOffer, behaviorSummary } = locationState;

    if (!salaryConfig || !dialogueHistory) {
      navigate('/', { replace: true });
      return;
    }

    try {
      setIsPreparing(true);
      setDisplayMessage('正在生成谈薪报告');

      // 1. 生成报告
      const report = await generateSalaryReport(
        salaryConfig,
        dialogueHistory,
        finalOffer,
        callDuration,
        behaviorSummary,
      );

      // 2. 更新展示文案，延迟2秒后跳转
      setDisplayMessage('报告已生成，正在跳转...');

      setTimeout(() => {
        // 跳转到谈薪报告页
        navigate('/salary-report', {
          replace: true,
          state: {
            report,
            dialogueHistory,
            salaryConfig,
            finalOffer,
            callDuration,
            behaviorSummary,
          },
        });
      }, 2000);
    } catch (error) {
      // 生成失败也跳转到报告页面（会显示默认报告）
      antdMessage.error('生成报告失败，但仍可查看报告');
      navigate('/salary-report', {
        replace: true,
        state: {
          report: null,
          dialogueHistory,
          salaryConfig,
          finalOffer,
          callDuration,
          behaviorSummary,
        },
      });
    } finally {
      setIsPreparing(false);
    }
  };

  // 根据模式选择图标
  const getIcon = () => {
    if (isSalaryMode || isGenerateSalaryReportMode) return 'wallet';
    if (isGenerateReportMode) return 'file';
    return 'sparkles';
  };

  // 获取面试/谈薪信息
  const jobInfo = isSalaryMode
    ? locationState.interviewConfig
      ? {
          position: locationState.interviewConfig.jobType,
          level: locationState.interviewConfig.level,
          type: '薪酬沟通',
        }
      : undefined
    : config
      ? {
          position: config.jobType,
          level: config.level,
          type: config.type,
        }
      : undefined;

  return (
    <LoadingTransition
      message={displayMessage}
      icon={getIcon()}
      jobInfo={jobInfo}
      isPressureInterview={config?.isPressureInterview}
    />
  );
}
